import { useMemo } from 'react';
import { NavigationNode, PageSearchResult, SEARCH_MIN_CHARS } from '@repo/types';
import { useNavigationsQuery } from '@data/navigations/useNavigationsQuery';
import { toHref, useActiveNav } from '../Nav/useActiveNav';

/** Breadcrumb separator, matching the design's `PageCard` example ("Stats › Players"). */
const TRAIL_SEPARATOR = ' › ';

/**
 * What the walk needs from a nav node, structurally.
 *
 * `useActiveNav` hands back `ActiveNavigationNode`, whose `children` is optional — so this takes
 * the shape both it and a raw `NavigationNode` satisfy rather than either concrete type.
 */
type SearchableNavNode = Pick<NavigationNode, 'label' | 'url' | 'sameTab'> & {
  visible?: boolean;
  children?: SearchableNavNode[];
};

/**
 * Depth-first walk that carries each node's breadcrumb trail, which is what the card's second line
 * shows — "Stats › Players" tells a fan where the page lives far better than `/stats/players` does.
 */
function flatten(nodes: SearchableNavNode[] | undefined, trail: string[] = []): PageSearchResult[] {
  if (!nodes?.length) {
    return [];
  }

  return nodes.flatMap((node) => {
    // A node hidden from the nav must stay hidden here too, or search becomes a way around it.
    if (node.visible === false) {
      return [];
    }

    const label = node.label?.trim();
    if (!label) {
      return flatten(node.children, trail);
    }

    const here = [...trail, label];
    // A parent that only groups children — a mega-menu heading — is a trail segment rather than a
    // destination, so it lends its label to descendants without becoming a result itself.
    //
    // `toHref` rather than the raw `url`: nav URLs are editor-supplied, and it is what every other
    // nav render passes them through — it adds a scheme to bare domains and collapses anything
    // failing the safe-href pattern to `#`, so a `javascript:` entry cannot reach an anchor.
    const self: PageSearchResult[] = node.url
      ? [{ title: label, path: toHref(node.url), trail: here.join(TRAIL_SEPARATOR), sameTab: node.sameTab }]
      : [];

    return [...self, ...flatten(node.children, here)];
  });
}

/**
 * The Pages section (SC-1096 D7).
 *
 * "A search for 'stats' must return the Stats page, not only the players on it." Matched in the
 * browser against the navigation the header already has loaded, so this costs no API leg, no new
 * collection query and no index — the tree is a handful of nodes and is already in the cache.
 *
 * Matching is substring on title and path, the same rule the server applies to the other sections.
 */
export function useSearchPages(query: string, limit: number): PageSearchResult[] {
  const { data } = useNavigationsQuery();

  // Emptiness, not nullishness: a tenant that configures only a mobile menu still has a
  // `desktopNodes: []`, and `??` would keep that empty array rather than falling through.
  const header = data?.header;
  const sourceNodes = header?.desktopNodes?.length ? header.desktopNodes : header?.mobileNodes;

  // `useActiveNav` rather than the raw nodes: it applies the scheduler, which a hand-rolled
  // `visible !== false` check does not. Without it a page scheduled off — absent from the nav a fan
  // is looking at — was still reachable through search.
  const visibleNodes = useActiveNav(sourceNodes);

  return useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (needle.length < SEARCH_MIN_CHARS) {
      return [];
    }

    const pages = flatten(visibleNodes);
    const seen = new Set<string>();

    return (
      pages
        .filter((page) => page.title.toLowerCase().includes(needle) || page.path.toLowerCase().includes(needle))
        // De-duplication runs AFTER the match, not during it. Two nav nodes can share a path under
        // different labels, and the title is half the match surface — claiming the path while
        // rejecting the node dropped a genuine later match ("Match Calendar → /calendar") in favour
        // of an earlier non-match ("Fixtures → /calendar").
        .filter((page) => {
          if (seen.has(page.path)) {
            return false;
          }
          seen.add(page.path);
          return true;
        })
        .slice(0, limit)
    );
  }, [visibleNodes, query, limit]);
}
